/**
 * Condição "Invisível" dos tokens da cena (BRAZ-24, docs/spec/table-media.md): o token continua no
 * mapa, mas só o recebem os mestres, quem o controla e quem foi liberado. Como o Ocultar, é feito
 * com as permissões do NDB: o servidor nem manda o token a quem não o vê. Cliente e servidor
 * usam as mesmas regras daqui.
 */
import { NDB_GROUPS } from './library.js';

/**
 * Atributo do token com a lista (JSON) de quem foi liberado para vê-lo; ausente, o token não está
 * invisível. O prefixo `cond_` fica para as outras condições dos tokens.
 */
export const INVISIBLE_ATTR = 'cond_invisible';
/** quantos liberados um token invisível guarda, no máximo */
export const MAX_INVISIBLE_VIEWERS = 50;

/** Quem foi liberado (null: não está invisível). */
export function readInvisible(value: unknown): string[] | null {
  if (typeof value !== 'string') return null;
  try {
    const list: unknown = JSON.parse(value);
    if (!Array.isArray(list)) return [];
    return [...new Set(list.filter((id): id is string => typeof id === 'string' && id.length > 0))].slice(0, MAX_INVISIBLE_VIEWERS);
  } catch {
    return [];
  }
}

/** O valor do atributo para estes liberados (null tira a condição). */
export const invisibleValue = (viewers: readonly string[] | null): string | null =>
  viewers ? JSON.stringify([...new Set(viewers)].slice(0, MAX_INVISIBLE_VIEWERS)) : null;

export type TokenReadState = {
  hidden: boolean;
  layer: string;
  /** os liberados (null: não está invisível) */
  invisible: readonly string[] | null;
  /** quem controla o token (tem `write` no nó) */
  controllers: readonly string[];
};
export type TokenReadPerm = { kind: 'group' | 'user'; id: string; allowance: 'deny' | 'strongAllow' | null };

/**
 * As entradas de leitura do nó do token. Oculto, na camada do mestre ou invisível, o grupo "todos"
 * perde a leitura. Invisível e à vista, quem controla e os liberados a recebem de volta com
 * `strongAllow`, que passa por cima do `deny` do grupo. O Ocultar prevalece: oculto, só os
 * mestres (donos do documento) o veem. `readers`: os usuários que já têm entrada de leitura no
 * nó; as que não valem mais são tiradas.
 */
export function tokenReadPerms(state: TokenReadState, readers: readonly string[]): TokenReadPerm[] {
  const concealed = state.hidden || state.layer === 'gm';
  const perms: TokenReadPerm[] = [{ kind: 'group', id: NDB_GROUPS.everyone, allowance: concealed || state.invisible ? 'deny' : null }];
  const allowed = new Set(state.invisible && !concealed ? [...state.controllers, ...state.invisible] : []);
  for (const id of allowed) perms.push({ kind: 'user', id, allowance: 'strongAllow' });
  for (const id of new Set(readers)) if (!allowed.has(id)) perms.push({ kind: 'user', id, allowance: null });
  return perms;
}

/** A pessoa vê o token, pela condição? (os mestres veem todos) */
export const seesInvisible = (state: Pick<TokenReadState, 'invisible' | 'controllers'>, userId: string): boolean =>
  !state.invisible || state.controllers.includes(userId) || state.invisible.includes(userId);
